import { useState, useRef, useEffect } from 'react';

interface Facility {
    FacilityCode: string;
    DisplayName: string;
}

interface Props {
    facilities: Facility[];
    onPick: (facilityCode: string, facilityName: string) => void;
}

/**
 * General (non-appointment) chat launcher for the carrier header.
 *
 * A general chat is scoped to carrier + one facility. Since a carrier may work
 * several facilities, clicking opens a facility picker; with a single facility
 * it opens that chat directly.
 */
export default function GeneralChatButton({ facilities, onPick }: Props) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const handleClick = () => {
        if (facilities.length === 1) {
            onPick(facilities[0].FacilityCode, facilities[0].DisplayName);
            return;
        }
        setOpen(o => !o);
    };

    return (
        <div ref={wrapRef} className="relative">
            <button
                onClick={handleClick}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-gray-200 bg-gray-50 text-gray-700 text-sm font-medium hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 transition-colors"
            >
                Chat
            </button>
            {open && facilities.length > 1 && (
                <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20 py-1">
                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                        Message which facility?
                    </div>
                    {facilities.map(f => (
                        <button
                            key={f.FacilityCode}
                            onClick={() => { onPick(f.FacilityCode, f.DisplayName); setOpen(false); }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                        >
                            {f.DisplayName}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
